import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Check, Loader2, Sparkles, CreditCard, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const PERKS = [
  "Unlimited techniques & sequences",
  "Video uploads and YouTube/Vimeo links",
  "Full drag-and-drop flowchart builder",
  "Create or join a gym & share with members",
];

export default function Billing() {
  const { user, refreshUser } = useAuth();
  const [billing, setBilling] = useState(user?.billing || null);
  const [loading, setLoading] = useState(false);
  const [polling, setPolling] = useState(false);
  const [params, setParams] = useSearchParams();
  const polled = useRef(false);

  const load = async () => {
    try { const { data } = await api.get("/billing/me"); setBilling(data); } catch (e) {}
  };
  useEffect(() => { load(); }, []);

  // Return from Stripe -> poll status
  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid || polled.current) return;
    polled.current = true;
    setPolling(true);
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/billing/status/${sid}`);
        if (data.status === "complete" || data.billing?.has_subscription) {
          setBilling(data.billing);
          await refreshUser();
          toast.success("Subscription active — you're all set!");
          setPolling(false);
          setParams({}, { replace: true });
          return;
        }
        if (data.status === "expired") { toast.error("Payment session expired."); setPolling(false); setParams({}, { replace: true }); return; }
      } catch (e) {}
      if (attempts >= 6) { setPolling(false); toast.message("Still processing — refresh in a moment."); setParams({}, { replace: true }); return; }
      setTimeout(poll, 2000);
    };
    poll();
    // eslint-disable-next-line
  }, []);

  const upgrade = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/billing/checkout", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(apiError(e)); setLoading(false); }
  };

  const manage = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(apiError(e)); setLoading(false); }
  };

  const isActive = (billing?.subscription_status === "active" || billing?.subscription_status === "trialing") && billing?.has_subscription && !billing?.requires_payment;
  const hasSub = billing?.has_subscription;
  const trialActive = billing?.trial_active;

  return (
    <div className="p-6 md:p-8 pb-24 md:pb-8 max-w-4xl">
      <p className="text-xs tracking-[0.25em] uppercase text-primary font-semibold mb-1">Billing</p>
      <h1 className="font-heading font-bold text-3xl tracking-tight mb-2">Your subscription</h1>

      {/* Status banner */}
      <div data-testid="billing-status" className="mb-8 mt-4 p-4 rounded-xl border bg-card flex items-center gap-3"
        style={{ borderColor: isActive ? "#10b981" : trialActive ? "#27272A" : "#E63946" }}>
        {polling ? <Loader2 className="h-5 w-5 text-primary animate-spin" /> :
          isActive ? <ShieldCheck className="h-5 w-5 text-emerald-400" /> : <Sparkles className="h-5 w-5 text-primary" />}
        <div className="text-sm">
          {polling ? <span>Confirming your subscription…</span> :
            isActive ? <span className="text-emerald-400 font-medium">
              {billing?.subscription_status === "trialing" ? `Free trial active — first charge on ${(billing?.current_period_end || billing?.trial_ends_at) ? new Date(billing.current_period_end || billing.trial_ends_at).toLocaleDateString() : "trial end"}.` :
               billing?.cancel_at_period_end ? `Subscription cancels on ${billing?.current_period_end ? new Date(billing.current_period_end).toLocaleDateString() : ""}.` :
               `Active — renews automatically on ${billing?.current_period_end ? new Date(billing.current_period_end).toLocaleDateString() : ""}.`}
            </span> :
            trialActive ? <span><span className="font-semibold text-white">{billing?.trial_days_left} day{billing?.trial_days_left === 1 ? "" : "s"}</span> left in your free trial.</span> :
            <span className="text-primary font-medium">Your free trial has ended. Subscribe to keep full access.</span>}
        </div>
      </div>

      {/* Plan card */}
      <div className="max-w-md rounded-2xl border border-primary/40 bg-gradient-to-br from-primary/10 to-card p-7">
        <div className="flex items-center gap-2 text-primary mb-1"><CreditCard className="h-5 w-5" /><span className="text-xs uppercase tracking-[0.2em] font-semibold">Infinite Sequence Pro</span></div>
        <div className="flex items-end gap-1 mt-3">
          <span className="font-heading font-black text-5xl tracking-tighter">$10</span>
          <span className="text-muted-foreground mb-1.5">/ month</span>
        </div>
        <p className="text-sm text-muted-foreground mt-1">7 days free, then $10/month billed automatically. Cancel anytime.</p>

        <ul className="mt-6 space-y-2.5">
          {PERKS.map((p) => (
            <li key={p} className="flex items-start gap-2.5 text-sm">
              <Check className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" /> <span className="text-foreground/90">{p}</span>
            </li>
          ))}
        </ul>

        {hasSub ? (
          <Button data-testid="manage-btn" onClick={manage} disabled={loading}
            className="w-full mt-7 h-11 bg-secondary hover:bg-secondary/80 text-white rounded font-semibold">
            {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Opening…</> : "Manage subscription"}
          </Button>
        ) : (
          <Button data-testid="upgrade-btn" onClick={upgrade} disabled={loading}
            className="w-full mt-7 h-11 bg-primary hover:bg-primary/90 text-white rounded font-semibold">
            {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Redirecting…</> :
              trialActive ? "Subscribe now" : "Subscribe — $10/month"}
          </Button>
        )}
        <p className="text-[11px] text-muted-foreground text-center mt-3" data-testid="stripe-mode-label">Secure checkout powered by Stripe{billing?.stripe_mode === "test" ? " · test mode" : ""}</p>
      </div>
    </div>
  );
}
